import React from "react";
import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  GridItemProps,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import OrderHistoryInvoiceAccordion from "@/features/my-order/features/order-history/components/invoice-section/OrderHistoryInvoiceAccordion";
import OrderHistoryInvoiceToggleBar from "@/features/my-order/features/order-history/components/OrderHistoryInvoiceToggleBar";
import OrderHistorySubPriceInfo from "@/features/my-order/features/order-history/components/OrderHistorySubPriceInfo";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import useGetOrderHistoryProformaInvoiceDetail from "@/features/my-order/features/order-history/hooks/useGetOrderHistoryProformaInvoiceDetail";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import { extractInvoiceFromOrderHistory } from "@/features/my-order/features/order-history/utils";
import { toCamelKeyObject } from "@/utils/case";
import { formatDateToYMDEGeneral, toLocaleKrw } from "@/shared/utils";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";

function OrderHistoryInvoiceProductPaymentArticle() {
  const { tTitle, tCurrencyKrw, tCurrencyString, isKor } = useI18nHelper();
  const { paymentForGoodAmount, paymentForGoodEurAmount, data } =
    useOrderHistoryProformaInvoice();
  // 원화·유로(바이어 유로 송금 프로포마) 합계를 각각 보여준다
  const paymentForGoodLabel = [
    paymentForGoodAmount || !paymentForGoodEurAmount
      ? tCurrencyKrw(paymentForGoodAmount ?? 0)
      : null,
    paymentForGoodEurAmount
      ? tCurrencyString(paymentForGoodEurAmount, {
          type: "SYMBOL",
          code: "EUR",
        })
      : null,
  ]
    .filter(Boolean)
    .join(" + ");
  const {
    data: downloadUnoFileData,
    fire: fireDownloadUnoFile,
    isLoading: downloadUnoFileIsLoading,
    clear: clearDownloadUnoFile,
    error: downloadUnoFileError,
  } = useDownloadUnoFile();

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex gap={"4px"}>
        <CustomIcon src={"package"} width={24} height={24} color={"#8F8F8C"} />
        <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
          {`${tTitle(
            "order_history:invoice.payment_for_goods"
          )} (${paymentForGoodLabel})`}
        </Text>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        {data?.proforma_invoices?.map((invoice, index) => {
          if (invoice.status === "REPLACED" || invoice.status === "CANCELED")
            return null;

          const proformaDetail = extractInvoiceFromOrderHistory(
            toCamelKeyObject(invoice)
          );
          const {
            totalCollectedKrwAmount,
            actualTotalCollectedKrwAmount,
            isRemittedToBoutique,
            isRemittedAdjustment,
            isRemittedToAgency,
            adjustmentAmount,
            showAdjustment,
            proformaFileId,
            adjustmentRemittedOn,
            adjustmentResolveRequired,
            getAdjustmentStatus,
            collectedCurrency,
          } = proformaDetail;

          const totalProformaAmount = isRemittedToBoutique
            ? actualTotalCollectedKrwAmount
            : totalCollectedKrwAmount;

          return (
            <OrderHistoryInvoiceToggleBar
              key={invoice.id}
              index={index}
              name={invoice.name}
              amount={totalProformaAmount}
              currency={collectedCurrency}
              defaultOpen={!isRemittedToAgency}
            >
              {({ isOpen }) => (
                <Flex flexDirection={"column"} gap={"8px"}>
                  <OrderHistoryInvoiceAccordion
                    isOpen={isOpen}
                    btnComponent={
                      <OrderHistorySubPriceInfo
                        variant={"PROFORMA"}
                        amount={totalCollectedKrwAmount}
                        currency={collectedCurrency}
                        onFileDownloadClick={() => {
                          if (!proformaFileId) return;
                          fireDownloadUnoFile(
                            {
                              unoFileId: proformaFileId,
                            },
                            {}
                          );
                        }}
                        status={
                          isRemittedToAgency
                            ? "REMITTED"
                            : "REMITTANCE_REQUIRED"
                        }
                      />
                    }
                  >
                    <ProformaDetailGrid proforma={proformaDetail} />
                  </OrderHistoryInvoiceAccordion>
                  {showAdjustment && (
                    <OrderHistoryInvoiceAccordion
                      isOpen={isOpen}
                      btnComponent={
                        <OrderHistorySubPriceInfo
                          variant={"PROFORMA_DIFFERENCE"}
                          amount={adjustmentAmount ?? 0}
                          status={getAdjustmentStatus() ?? "ERROR"}
                        />
                      }
                    >
                      {isRemittedAdjustment && (
                        <Box padding={"8px 0px 12px 44px"}>
                          <Box
                            padding={"16px"}
                            borderRadius={"8px"}
                            boxShadow={"0 0 0 0.5px #D4D4D1"}
                          >
                            <UnorderedList
                              textStyle={"caption-regular"}
                              color={"#6F6F6C"}
                            >
                              <ListItem>
                                {tTitle(
                                  `order_history:invoice.${
                                    getAdjustmentStatus() === "RETURNED"
                                      ? "returned_date"
                                      : "remitted_date"
                                  }`
                                )}{" "}
                                :{" "}
                                {formatDateToYMDEGeneral(
                                  adjustmentRemittedOn as string,
                                  isKor,
                                  "-"
                                )}
                              </ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                      )}
                    </OrderHistoryInvoiceAccordion>
                  )}
                </Flex>
              )}
            </OrderHistoryInvoiceToggleBar>
          );
        })}
      </Flex>
    </Flex>
  );
}

export default OrderHistoryInvoiceProductPaymentArticle;

interface ProformaDetailGridProps {
  proforma: ReturnType<typeof extractInvoiceFromOrderHistory>;
}

const proformaGridTemplateColumns = `repeat(3, 1fr)`;

const ProformaDetailGrid = ({ proforma }: ProformaDetailGridProps) => {
  const { tTitle, tCurrencyString, isKor } = useI18nHelper();
  const {
    data,
    currency,
    isKrwInvoice,
    isBuyerToAgencyEur,
    invoiceAmount,
    usedDeposit,
    finalInvoiceAmount,
    usedCredit,
    totalEuroAmount,
    exchangeRate,
    actualExchangeRate,
    totalKrwAmount,
    actualTotalKrwAmount,
    bankFee,
    actualBankFee,
    totalCollectedKrwAmount,
    actualTotalCollectedKrwAmount,
    showFinalInvoice,
    isRemittedToAgency,
    isRemittedToBoutique,
    issuedOn,
    remittedOn,
  } = proforma;

  // 인보이스 금액은 프로포마 통화로 보여준다(원화 프로포마면 원화)
  const money = { type: "SYMBOL" as const, code: currency };

  return (
    <Box padding={"8px 0px 12px 44px"}>
      <Grid
        templateColumns={proformaGridTemplateColumns}
        padding={"16px"}
        borderRadius={"8px"}
        boxShadow={"0 0 0 0.5px #D4D4D1"}
        sx={{
          "& > *:nth-of-type(3n + 2), & > *:nth-of-type(3n + 3)": {
            textAlign: "end",
          },
          "& > *:nth-of-type(3n + 1)": {
            paddingLeft: "8px",
          },
          "& > *:nth-of-type(3n + 3)": {
            paddingRight: "8px",
          },
        }}
        alignItems={"center"}
      >
        <TitleGridItem paddingBottom={"2px"}>
          {tTitle("order_history:invoice.category")}
        </TitleGridItem>
        <TitleGridItem paddingBottom={"2px"}>
          {tTitle("order_history:invoice.requested_amount")}
        </TitleGridItem>
        <TitleGridItem paddingBottom={"2px"}>
          {tTitle("order_history:invoice.remitted_amount")}
        </TitleGridItem>

        <ValueGridItem>
          {tTitle("order_history:invoice.invoice_amount")}
        </ValueGridItem>
        <ValueGridItem>{tCurrencyString(invoiceAmount, money)}</ValueGridItem>
        <ValueGridItem>
          {isRemittedToBoutique ? tCurrencyString(invoiceAmount, money) : "-"}
        </ValueGridItem>

        {data?.usedDeposit?.value !== 0 && (
          <>
            <ValueGridItem>
              {tTitle("order_history:invoice.used_deposit")}
            </ValueGridItem>
            <ValueGridItem>
              - {tCurrencyString(usedDeposit, money)}
            </ValueGridItem>
            <ValueGridItem>
              {isRemittedToBoutique
                ? `- ${tCurrencyString(usedDeposit, money)}`
                : "-"}
            </ValueGridItem>
          </>
        )}

        {showFinalInvoice && (
          <>
            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>
            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>
            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>
          </>
        )}

        {showFinalInvoice && (
          <>
            <ValueGridItem>
              {tTitle("order_history:invoice.final_invoice_amount")}
            </ValueGridItem>
            <ValueGridItem>
              {tCurrencyString(finalInvoiceAmount, money)}
            </ValueGridItem>
            <ValueGridItem>
              {isRemittedToBoutique
                ? tCurrencyString(finalInvoiceAmount, money)
                : "-"}
            </ValueGridItem>
          </>
        )}

        {usedCredit !== 0 && (
          <>
            <ValueGridItem>
              {tTitle("order_history:invoice.used_credit")}
            </ValueGridItem>
            <ValueGridItem>
              - {tCurrencyString(usedCredit, money)}
            </ValueGridItem>
            <ValueGridItem>
              {isRemittedToBoutique
                ? `- ${tCurrencyString(usedCredit, money)}`
                : "-"}
            </ValueGridItem>
          </>
        )}

        {showFinalInvoice && (
          <>
            <TitleGridItem
              paddingY={"6px"}
              background={"#F7F9FA"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              marginTop={"4px"}
            >
              {tTitle("order_history:invoice.total_euro_amount")}
            </TitleGridItem>
            <ValueGridItem
              paddingY={"6px"}
              background={"#F7F9FA"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              marginTop={"4px"}
            >
              {tCurrencyString(totalEuroAmount, money)}
            </ValueGridItem>
            <ValueGridItem
              paddingY={"6px"}
              background={"#F7F9FA"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              marginTop={"4px"}
            >
              {isRemittedToBoutique
                ? tCurrencyString(totalEuroAmount, money)
                : "-"}
            </ValueGridItem>
          </>
        )}

        {/* 바이어 유로 송금 프로포마는 환전이 없다 — 환율·원화 환산·은행 수수료 대신 유로로 낼 금액만 보여준다 */}
        {isBuyerToAgencyEur && (
          <>
            <TitleGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              marginTop={"4px"}
            >
              {tTitle("order_history:invoice.total_collected_eur_amount")}
            </TitleGridItem>
            <ValueGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              textStyle={"body-md-semi-bold"}
              color={isRemittedToAgency ? "#444444" : "#CC1D1D"}
              marginTop={"4px"}
              lineHeight={"20px"}
            >
              {tCurrencyString(totalCollectedKrwAmount, money)}
            </ValueGridItem>
            <ValueGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              textStyle={"body-md-semi-bold"}
              color={"#1272EF"}
              marginTop={"4px"}
              lineHeight={"20px"}
            >
              {isRemittedToBoutique
                ? tCurrencyString(actualTotalCollectedKrwAmount, money)
                : "-"}
            </ValueGridItem>
          </>
        )}

        {/* 원화 프로포마는 환전·해외 송금이 없다 — 인보이스 금액만 보여주고 환율·원화 환산 금액은 감춘다 */}
        {!isKrwInvoice && !isBuyerToAgencyEur && (
          <>
            <ValueGridItem>{tTitle("common:exchange_rate")}</ValueGridItem>
            <ValueGridItem>{toLocaleKrw(exchangeRate, 2, 2)}</ValueGridItem>
            <ValueGridItem>
              {isRemittedToBoutique
                ? toLocaleKrw(actualExchangeRate, 2, 2)
                : "-"}
            </ValueGridItem>

            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>
            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>
            <ValueGridItem paddingY={"4px"}>
              <Divider variant={"dashed"} color={"#D4D4D1"} opacity={1} />
            </ValueGridItem>

            <ValueGridItem>
              {tTitle("order_history:invoice.total_krw_amount")}
            </ValueGridItem>
            <ValueGridItem>{toLocaleKrw(totalKrwAmount)}</ValueGridItem>
            <ValueGridItem>
              {/* 원 미만 절사 — toLocaleKrw 는 반올림해 표시하므로 은행 출금액과 1원 어긋난다 */}
              {isRemittedToBoutique ? toLocaleKrw(Math.floor(actualTotalKrwAmount)) : "-"}
            </ValueGridItem>

            <ValueGridItem>{tTitle("common:bank_fee")}</ValueGridItem>
            <ValueGridItem>{toLocaleKrw(bankFee)}</ValueGridItem>
            <ValueGridItem>
              {isRemittedToBoutique ? toLocaleKrw(actualBankFee) : "-"}
            </ValueGridItem>

            <TitleGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              marginTop={"4px"}
            >
              {tTitle("order_history:invoice.total_collected_krw_amount")}
            </TitleGridItem>
            <ValueGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              textStyle={"body-md-semi-bold"}
              color={isRemittedToAgency ? "#444444" : "#CC1D1D"}
              marginTop={"4px"}
              lineHeight={"20px"}
            >
              {toLocaleKrw(totalCollectedKrwAmount)}
            </ValueGridItem>
            <ValueGridItem
              paddingY={"10px"}
              background={"#FFF7CC"}
              boxShadow={"0 -0.5px 0 0 #D4D4D1"}
              textStyle={"body-md-semi-bold"}
              color={"#1272EF"}
              marginTop={"4px"}
              lineHeight={"20px"}
            >
              {isRemittedToBoutique
                ? toLocaleKrw(actualTotalCollectedKrwAmount)
                : "-"}
            </ValueGridItem>
          </>
        )}

        <GridItem paddingTop={"12px"}>
          <UnorderedList textStyle={"caption-regular"} color={"#6F6F6C"}>
            <ListItem>
              {tTitle("order_history:invoice.issued_date")} :{" "}
              {formatDateToYMDEGeneral(issuedOn as string, isKor, "-")}
            </ListItem>
            {remittedOn && (
              <ListItem>
                {tTitle("order_history:invoice.remitted_date")} :{" "}
                {formatDateToYMDEGeneral(remittedOn as string, isKor, "-")}
              </ListItem>
            )}
          </UnorderedList>
        </GridItem>
      </Grid>
    </Box>
  );
};

interface CommonGridItemProps extends GridItemProps {
  children?: React.ReactNode;
}

const TitleGridItem = ({ children, ...props }: CommonGridItemProps) => {
  return (
    <GridItem textStyle={"body-sm-medium"} color={"#444444"} {...props}>
      {children}
    </GridItem>
  );
};

const ValueGridItem = ({ children, ...props }: CommonGridItemProps) => {
  return (
    <GridItem
      textStyle={"body-sm-regular"}
      color={"#6F6F6C"}
      paddingY={"2px"}
      {...props}
    >
      {children}
    </GridItem>
  );
};
